import {
  CollectionDocument,
  CollectionMembershipDocument,
  CollectionSession,
  CollectionSummary,
  parseCollectionDocument,
  parseCollectionMembershipDocument,
} from '@qkindex/contracts';
import {
  DocumentSnapshot,
  collectionGroup,
  getDoc,
  getDocs,
  limit,
  orderBy,
  onSnapshot,
  query,
  startAfter,
  where,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';

export const COLLECTION_PAGE_SIZE_MIN = 1;
export const COLLECTION_PAGE_SIZE_MAX = 50;

const boundedPageSize = (requested: number): number =>
  Math.max(
    COLLECTION_PAGE_SIZE_MIN,
    Math.min(COLLECTION_PAGE_SIZE_MAX, Math.trunc(requested)),
  );

export const isExpectedInaccessibleCollectionError = (
  error: unknown,
): boolean => {
  if (!error || typeof error !== 'object' || !('code' in error)) return false;
  const { code } = error as { code?: unknown };
  return code === 'permission-denied' || code === 'firestore/permission-denied';
};

export interface CollectionRepository {
  get(collectionId: string): Promise<CollectionDocument | undefined>;
  getMembership(
    collectionId: string,
    userId: string,
  ): Promise<CollectionMembershipDocument | undefined>;
  loadSession(
    userId: string,
    pageSize?: number,
    cursor?: string,
  ): Promise<CollectionSession>;
}

export const firebaseCollectionRepository: CollectionRepository = {
  async get(collectionId) {
    const snapshot = await getDoc(doc(db, 'collections', collectionId));
    return snapshot.exists()
      ? parseCollectionDocument(snapshot.data())
      : undefined;
  },

  async getMembership(collectionId, userId) {
    const snapshot = await getDoc(
      doc(db, 'collections', collectionId, 'members', userId),
    );
    return snapshot.exists()
      ? parseCollectionMembershipDocument(snapshot.data())
      : undefined;
  },

  async loadSession(userId, requestedPageSize = 20, cursor) {
    const constraints = [
      where('userId', '==', userId),
      where('status', '==', 'active'),
      orderBy('collectionId'),
      ...(cursor ? [startAfter(cursor)] : []),
      limit(boundedPageSize(requestedPageSize) + 1),
    ];
    const memberships = await getDocs(
      query(collectionGroup(db, 'members'), ...constraints),
    );
    const visible = memberships.docs.slice(
      0,
      boundedPageSize(requestedPageSize),
    );
    const summaries = (
      await Promise.all(
        visible.map(
          async (
            membershipSnapshot,
          ): Promise<CollectionSummary | undefined> => {
            const membership = parseCollectionMembershipDocument(
              membershipSnapshot.data(),
            );
            let collectionSnapshot;
            try {
              collectionSnapshot = await getDoc(
                doc(db, 'collections', membership.collectionId),
              );
            } catch (error) {
              if (isExpectedInaccessibleCollectionError(error))
                return undefined;
              throw error;
            }
            if (!collectionSnapshot.exists()) return undefined;
            const collection = parseCollectionDocument(
              collectionSnapshot.data(),
            );
            if (
              collection.lifecycle !== 'active' &&
              collection.lifecycle !== 'transfer-pending'
            )
              return undefined;
            // Owner names are optional presentation, resolved by the visible
            // collection picker rather than holding up inventory discovery.
            return {
              collectionId: membership.collectionId,
              name: collection.name,
              role: membership.role,
              lifecycle: collection.lifecycle,
              lifecycleVersion: collection.lifecycleVersion,
              membershipVersion: membership.membershipVersion,
              ownerUserId: collection.ownerUserId,
              ...(collection.gps ? { gps: collection.gps } : {}),
            };
          },
        ),
      )
    ).filter((summary): summary is CollectionSummary => summary !== undefined);
    return {
      userId,
      availableCollections: {
        items: summaries,
        ...(memberships.docs.length > visible.length && visible.length
          ? {
              nextCursor: parseCollectionMembershipDocument(
                visible[visible.length - 1].data(),
              ).collectionId,
            }
          : {}),
      },
    };
  },
};

export const subscribeToCollectionMembership = (
  collectionId: string,
  userId: string,
  onMembership: (membership: CollectionMembershipDocument | undefined) => void,
  onError: () => void,
  requireServer = false,
): (() => void) => {
  const next = (snapshot: DocumentSnapshot) => {
    if (
      requireServer &&
      (snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites)
    )
      return;
    if (!snapshot.exists()) {
      onMembership(undefined);
      return;
    }
    try {
      const membership = parseCollectionMembershipDocument(snapshot.data());
      if (
        membership.collectionId !== collectionId ||
        membership.userId !== userId
      ) {
        onError();
        return;
      }
      onMembership(membership);
    } catch {
      onError();
    }
  };
  const reference = doc(db, 'collections', collectionId, 'members', userId);
  return requireServer
    ? onSnapshot(reference, { includeMetadataChanges: true }, next, onError)
    : onSnapshot(reference, next, onError);
};

export const subscribeToCollection = (
  collectionId: string,
  onCollection: (collection: CollectionDocument | undefined) => void,
  onError: () => void,
  requireServer = false,
): (() => void) => {
  const next = (snapshot: DocumentSnapshot) => {
    if (
      requireServer &&
      (snapshot.metadata.fromCache || snapshot.metadata.hasPendingWrites)
    )
      return;
    if (!snapshot.exists()) {
      onCollection(undefined);
      return;
    }
    try {
      onCollection(parseCollectionDocument(snapshot.data()));
    } catch {
      onError();
    }
  };
  const reference = doc(db, 'collections', collectionId);
  return requireServer
    ? onSnapshot(reference, { includeMetadataChanges: true }, next, onError)
    : onSnapshot(reference, next, onError);
};
